import { motion, useInView } from 'motion/react';
import { useRef, useState } from 'react';
import { X, Check, ArrowRight, Sparkles } from 'lucide-react';

export function TransformationSplit() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [hoveredSide, setHoveredSide] = useState<'before' | 'after' | null>(null);

  const beforeItems = [
    'Dados espalhados em 7+ ferramentas diferentes',
    'Relatórios manuais que levam dias',
    'Decisões baseadas em "achismos"',
    'Bots inflando métricas em 43%',
    'ROI das campanhas é um mistério',
    'Equipe perdida em planilhas infinitas',
  ];

  const afterItems = [
    'Tudo centralizado em 1 dashboard intuitivo',
    'Relatórios automáticos em tempo real',
    'Decisões baseadas em dados precisos',
    'Bot Killer bloqueia 100% dos falsos',
    'ROI calculado automaticamente',
    'Equipe focada em crescimento, não em planilhas',
  ];

  return (
    <section ref={ref} className="relative py-24 bg-[#0B0E14] overflow-hidden">
      {/* Divider Line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#4ECDC4] to-transparent" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4ECDC4]/30 bg-[#4ECDC4]/10 backdrop-blur-sm mb-6">
            <Sparkles className="w-4 h-4 text-[#4ECDC4]" />
            <span className="text-[#4ECDC4] font-mono text-sm uppercase tracking-wider">
              A Transformação
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            Antes vs. Depois
            <br />
            <span className="bg-gradient-to-r from-[#4ECDC4] to-[#10B981] bg-clip-text text-transparent">
              A Diferença é Brutal
            </span>
          </h2>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Compare como era operar <span className="text-[#FF6B35]">sem WPTT</span> vs. 
            <span className="text-[#4ECDC4]"> com controle total</span>
          </p>
        </motion.div>

        {/* Split Comparison */}
        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* BEFORE Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            onMouseEnter={() => setHoveredSide('before')}
            onMouseLeave={() => setHoveredSide(null)}
            className="relative"
          >
            <div className={`
              p-8 rounded-3xl border-2 transition-all duration-300
              ${hoveredSide === 'before' 
                ? 'border-[#FF6B35] bg-gradient-to-br from-[#FF6B35]/10 to-transparent scale-105' 
                : 'border-[#FF6B35]/30 bg-[#0A0E1A]/50'
              }
            `}>
              {/* Header */}
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/20 border border-[#FF6B35]/50 flex items-center justify-center">
                  <X className="w-6 h-6 text-[#FF6B35]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#FF6B35]" style={{ fontFamily: 'Sora, sans-serif' }}>
                    ANTES
                  </h3>
                  <p className="text-gray-500 text-sm font-mono">Caos Total</p>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                {beforeItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    className="flex items-start gap-3 group"
                  >
                    <X className="w-5 h-5 text-[#FF6B35] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 group-hover:text-white transition-colors">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-8 pt-8 border-t border-[#FF6B35]/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-xl bg-[#FF6B35]/10">
                    <div className="text-2xl font-bold text-[#FF6B35]">67h</div>
                    <div className="text-xs text-gray-400 mt-1">Perdidas/mês</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#FF6B35]/10">
                    <div className="text-2xl font-bold text-[#FF6B35]">-R$ 47k</div>
                    <div className="text-xs text-gray-400 mt-1">Prejuízo/mês</div>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              {hoveredSide === 'before' && (
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF6B35]/20 to-transparent blur-2xl -z-10" />
              )}
            </div>
          </motion.div>

          {/* AFTER Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            onMouseEnter={() => setHoveredSide('after')}
            onMouseLeave={() => setHoveredSide(null)}
            className="relative"
          >
            <div className={`
              p-8 rounded-3xl border-2 transition-all duration-300
              ${hoveredSide === 'after' 
                ? 'border-[#4ECDC4] bg-gradient-to-br from-[#4ECDC4]/10 to-transparent scale-105' 
                : 'border-[#4ECDC4]/30 bg-[#0A0E1A]/50'
              }
            `}>
              {/* Header */}
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/20 border border-[#4ECDC4]/50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#4ECDC4]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#4ECDC4]" style={{ fontFamily: 'Sora, sans-serif' }}>
                    DEPOIS
                  </h3>
                  <p className="text-gray-500 text-sm font-mono">Controle Total</p>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                {afterItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    className="flex items-start gap-3 group"
                  >
                    <Check className="w-5 h-5 text-[#4ECDC4] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 group-hover:text-white transition-colors">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <div className="mt-8 pt-8 border-t border-[#4ECDC4]/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-xl bg-[#4ECDC4]/10">
                    <div className="text-2xl font-bold text-[#4ECDC4]">+156h</div>
                    <div className="text-xs text-gray-400 mt-1">Economizadas</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#10B981]/10">
                    <div className="text-2xl font-bold text-[#10B981]">+R$ 89k</div>
                    <div className="text-xs text-gray-400 mt-1">Lucro extra</div>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              {hoveredSide === 'after' && (
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#4ECDC4]/20 to-transparent blur-2xl -z-10" />
              )}
            </div>
          </motion.div>
        </div>

        {/* Arrow Transition */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 hidden md:block"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#FF6B35] via-[#FFE66D] to-[#4ECDC4] flex items-center justify-center shadow-2xl">
            <ArrowRight className="w-8 h-8 text-white" />
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1 }}
          className="text-center mt-16"
        >
          <p className="text-2xl text-gray-300 mb-6">
            Pronto para sua <span className="text-[#4ECDC4] font-semibold">transformação</span>?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-5 bg-gradient-to-r from-[#4ECDC4] to-[#10B981] rounded-xl font-bold text-white text-lg shadow-2xl"
          >
            Iniciar Transformação Agora
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}
